import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';
import { PhoneReveal } from '@/components/PhoneReveal';
import { HeroLead, PageHero, Spinner } from '@/components/ui';
import { COMPANY } from '@/content/company';
import { submitEnquiry, type Enquiry } from '@/lib/enquiry';
import { getCaptchaToken, loadCaptcha } from '@/lib/recaptcha';
import styles from './Contact.module.css';

/**
 * Name chosen because it is the kind of field a naive bot expects to find and
 * fill in. A real visitor never sees it: it is off-screen, untabbable and
 * hidden from assistive technology, so anything arriving in it came from
 * something reading the markup rather than the page.
 */
const HONEYPOT_FIELD = 'website';

const INTERESTS = [
  'eCommerce build',
  'Platform app',
  'Integration',
  'Web application',
  'Consulting',
  'Roblox',
  'Something else',
] as const;

type FieldName = 'name' | 'email' | 'description';
type Errors = Partial<Record<FieldName, string>>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(values: Enquiry): Errors {
  const errors: Errors = {};

  if (!values.name.trim()) {
    errors.name = 'Enter your name so we know who we are replying to.';
  }

  if (!values.email.trim()) {
    errors.email = 'Enter an email address - without one we cannot reply.';
  } else if (!EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = 'That does not look like an email address. Check it and try again.';
  }

  if (!values.description.trim()) {
    errors.description = 'Tell us a little about the project, even a couple of lines.';
  }

  return errors;
}

export function Contact() {
  return (
    <>
      <PageHero
        variant="contact"
        eyebrow="Contact"
        title="Tell us what you are building."
        lead={
          <HeroLead narrow>
            A few lines about the project is enough to start. We reply personally, usually within
            one working day.
          </HeroLead>
        }
      />

      <div className={styles.body}>
        <EnquiryForm />
        <ContactAside />
      </div>
    </>
  );
}

/* -------------------------------------------------------------------- form */

function EnquiryForm() {
  const [values, setValues] = useState<Enquiry>({
    name: '',
    company: '',
    email: '',
    interest: INTERESTS[0],
    description: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState('');

  const formRef = useRef<HTMLFormElement>(null);
  const confirmationRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  /*
   * True only for a state change this visitor just caused, so focus is moved
   * when a control they pressed was replaced - and never on a first render,
   * where focusing anything would be an unexplained jump on a page nobody has
   * interacted with yet. Same guard as PhoneReveal's `justRevealed`.
   */
  const justSubmitted = useRef(false);
  const justReset = useRef(false);

  /*
   * Focus moves here, in an effect, rather than in the callback that set the
   * state - and that is not a stylistic preference.
   *
   * Sending replaces the whole form with the confirmation, which removes the
   * submit button the visitor is standing on. The browser's own "the focused
   * element is gone, reset to body" runs asynchronously, so a focus() scheduled
   * as a macrotask - which is what the `setTimeout(..., 0)` here used to be -
   * races it and loses about as often as it wins. Measured in a real browser it
   * lost: activeElement came back as <body>, dumping a keyboard or screen-reader
   * visitor at the top of the document at the exact moment they wanted the
   * outcome. jsdom cannot see this, and the axe sweep did not assert focus, so
   * every test passed.
   *
   * An effect runs in the same commit as the removal, leaving no window for the
   * reset to land in. PhoneReveal.tsx hit this first and its comment records the
   * same finding; this is the other half of it.
   */
  useEffect(() => {
    if (sent && justSubmitted.current) {
      justSubmitted.current = false;
      confirmationRef.current?.focus();
    }
    // Returning to a blank form removes the "Send another message" button that
    // was just pressed, so the same reasoning applies in the other direction.
    if (!sent && justReset.current) {
      justReset.current = false;
      firstFieldRef.current?.focus();
    }
  }, [sent]);

  /*
   * When the form became fillable, stamped in an effect rather than during
   * render: Date.now() is impure, and on a prerendered page a render-time value
   * would be build time rather than the visitor's browser.
   *
   * Null until the effect runs. submitEnquiry omits the signal entirely in that
   * case, and the backend treats a missing one as "not a bot" - the safe way for
   * this to fail.
   */
  const shownAt = useRef<number | null>(null);
  useEffect(() => {
    shownAt.current = Date.now();

    /*
     * Warm the captcha up now so the round trip to Google is already paid for by
     * the time someone presses send, rather than being added to their wait.
     *
     * This is also the only place the site talks to a third party, and it is
     * scoped to this one route on purpose - a visitor who never opens /contact is
     * never announced to Google. A rejection here is deliberately swallowed:
     * getCaptchaToken() retries on submit and returns null if it still cannot,
     * and the backend decides what a missing token means.
     */
    void loadCaptcha().catch(() => {
      // Nothing to do here. Logging it would put noise in a visitor's console
      // for a condition the submit path already handles.
    });
  }, []);

  const ids = {
    name: useId(),
    company: useId(),
    email: useId(),
    description: useId(),
    interest: useId(),
    honeypot: useId(),
  };

  const set = (field: keyof Enquiry) => (value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    // Clear a field's error as soon as the visitor starts fixing it, rather
    // than making them re-submit to find out whether they got it right.
    if (field in errors) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as FieldName];
        return next;
      });
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    /*
     * The submit button is aria-disabled rather than `disabled` while sending, so
     * that a keyboard visitor is not thrown to the top of the document the moment
     * they press it. An aria-disabled button still fires, so the guard that
     * `disabled` used to provide has to live here - without it a double-click
     * sends the enquiry twice.
     */
    if (submitting) return;

    setSubmitError(null);

    const found = validate(values);
    setErrors(found);

    const firstInvalid = (Object.keys(found) as FieldName[])[0];
    if (firstInvalid) {
      // Focus goes to the first problem so a keyboard or screen-reader user is
      // put where the fix is, not left at the submit button.
      formRef.current?.querySelector<HTMLElement>(`#${CSS.escape(ids[firstInvalid])}`)?.focus();
      return;
    }

    setSubmitting(true);
    // The token is minted at submit time, not at page load: v3 tokens are
    // single-use and expire after two minutes, so one grabbed on mount would be
    // stale for anyone who reads the page before writing.
    void getCaptchaToken()
      .then((captchaToken) =>
        submitEnquiry(values, {
          honeypot,
          elapsedMs: shownAt.current === null ? undefined : Date.now() - shownAt.current,
          captchaToken,
        }),
      )
      .then((result) => {
        if (result.ok) {
          // Read by the effect above, which is what actually moves focus.
          justSubmitted.current = true;
          setSent(true);
        } else {
          setSubmitError(result.error);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  if (sent) {
    return (
      <div
        className={styles.confirmation}
        ref={confirmationRef}
        tabIndex={-1}
        role="status"
        aria-live="polite"
      >
        <div className={styles.confirmationMark} aria-hidden="true">
          ✓
        </div>
        <h2 className={styles.confirmationTitle}>Message sent</h2>
        <p className={styles.confirmationText}>
          Thanks{values.name.trim() ? `, ${values.name.trim().split(' ')[0]}` : ''} - we have your
          enquiry and will reply personally, usually within one working day.
        </p>
        <button
          type="button"
          className={styles.confirmationReset}
          onClick={() => {
            // Read by the effect above; this button is about to be unmounted.
            justReset.current = true;
            setSent(false);
            setValues({
              name: '',
              company: '',
              email: '',
              interest: INTERESTS[0],
              description: '',
            });
            setHoneypot('');
          }}
        >
          Send another message
        </button>
      </div>
    );
  }

  const describedBy = (field: FieldName) => (errors[field] ? `${ids[field]}-error` : undefined);

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate ref={formRef}>
      <h2 className="sk-visually-hidden">Project enquiry</h2>

      {submitError && (
        <p className={styles.errorSummary} role="alert">
          {submitError}
        </p>
      )}

      {/*
       * Honeypot. aria-hidden keeps it out of the accessibility tree and
       * tabIndex -1 keeps it out of the tab order, which together mean no
       * keyboard or screen-reader user can reach it - and satisfy axe's rule
       * against focusable content inside aria-hidden. It still carries a real
       * label so the markup stays valid for anything that does read it.
       */}
      <div className={styles.honeypot} aria-hidden="true">
        <label htmlFor={ids.honeypot}>Website</label>
        <input
          id={ids.honeypot}
          type="text"
          name={HONEYPOT_FIELD}
          autoComplete="off"
          tabIndex={-1}
          value={honeypot}
          onChange={(e) => {
            setHoneypot(e.target.value);
          }}
        />
      </div>

      <div className={styles.row}>
        <div>
          <label className={styles.label} htmlFor={ids.name}>
            Name
          </label>
          <input
            id={ids.name}
            ref={firstFieldRef}
            className={styles.input}
            type="text"
            name="name"
            autoComplete="name"
            placeholder="Jana Novák"
            value={values.name}
            onChange={(e) => {
              set('name')(e.target.value);
            }}
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={describedBy('name')}
            required
          />
          {errors.name && (
            <span className={styles.error} id={`${ids.name}-error`}>
              {errors.name}
            </span>
          )}
        </div>

        <div>
          <label className={styles.label} htmlFor={ids.company}>
            Company <span className={styles.optional}>(optional)</span>
          </label>
          <input
            id={ids.company}
            className={styles.input}
            type="text"
            name="company"
            autoComplete="organization"
            placeholder="Company name"
            value={values.company}
            onChange={(e) => {
              set('company')(e.target.value);
            }}
          />
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor={ids.email}>
          Email
        </label>
        <input
          id={ids.email}
          className={styles.input}
          type="email"
          name="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={values.email}
          onChange={(e) => {
            set('email')(e.target.value);
          }}
          aria-invalid={errors.email ? true : undefined}
          aria-describedby={describedBy('email')}
          required
        />
        {errors.email && (
          <span className={styles.error} id={`${ids.email}-error`}>
            {errors.email}
          </span>
        )}
      </div>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Area of interest</legend>
        <div className={styles.chips}>
          {INTERESTS.map((interest) => {
            const id = `${ids.interest}-${interest.replace(/\W+/g, '-')}`;
            return (
              <div className={styles.chipWrap} key={interest}>
                <input
                  className={styles.chipInput}
                  type="radio"
                  id={id}
                  name="interest"
                  value={interest}
                  checked={values.interest === interest}
                  onChange={() => {
                    set('interest')(interest);
                  }}
                />
                <label className={styles.chipLabel} htmlFor={id}>
                  {interest}
                </label>
              </div>
            );
          })}
        </div>
      </fieldset>

      <div className={styles.field}>
        <label className={styles.label} htmlFor={ids.description}>
          Project description
        </label>
        <textarea
          id={ids.description}
          className={styles.textarea}
          name="description"
          rows={6}
          placeholder="What are you building, what is in place today, and what needs to change?"
          value={values.description}
          onChange={(e) => {
            set('description')(e.target.value);
          }}
          aria-invalid={errors.description ? true : undefined}
          aria-describedby={describedBy('description')}
          required
        />
        {errors.description && (
          <span className={styles.error} id={`${ids.description}-error`}>
            {errors.description}
          </span>
        )}
      </div>

      <div className={styles.formFooter}>
        <div className={`${styles.consent} sk-prose-link`}>
          {/*
           * Deliberately a statement, not a request for permission. The previous
           * wording - "you agree that we may use your details to reply" - asked
           * for consent, while the Privacy Policy relies on Art. 6(1)(b),
           * pre-contractual steps. Asking for a consent you are not relying on
           * is the standard way of ending up owing the duties that come with
           * one: a withdrawal mechanism, and the argument that everything done
           * on the real basis was actually done on the fictional one.
           */}
          <p className={styles.consentLine}>
            We use what you send only to reply to this enquiry. See our{' '}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>
          {/*
           * Required wording, not decoration. Google's terms allow the floating
           * reCAPTCHA badge to be hidden only if the attribution appears in the
           * form instead - see the .grecaptcha-badge rule in global.css. Text in
           * the reading flow is also the more honest of the two: a corner badge
           * is the kind of thing people have learned not to see.
           *
           * New tab because losing a part-written enquiry to a policy link would
           * be its own small betrayal.
           */}
          <p className={styles.consentLine}>
            Protected by reCAPTCHA. The Google{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>{' '}
            apply.
          </p>
        </div>
        <button type="submit" className={styles.submit} aria-disabled={submitting || undefined}>
          {submitting ? (
            <>
              <Spinner /> Sending…
            </>
          ) : (
            'Send message'
          )}
        </button>
        {/*
         * The label swap conveys the wait to someone looking at the button; this
         * is the same information for someone who is not. Emptied when sending
         * ends, because the outcome is announced by the confirmation's own live
         * region or by the error alert above.
         *
         * A sibling of the button rather than wrapped with it: .sk-visually-hidden
         * is absolutely positioned, so it is not a flex item of .formFooter and
         * cannot open a gap in a layout that spaces its children apart.
         */}
        <span className="sk-visually-hidden" role="status">
          {submitting ? 'Sending your message…' : ''}
        </span>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------- aside */

function ContactAside() {
  return (
    <div className={styles.aside}>
      <section className={styles.asideCard} aria-labelledby="direct-heading">
        <h2 id="direct-heading" className={styles.asideHeading}>
          Direct
        </h2>
        <p className={styles.asideEmail}>
          <a href={`mailto:${COMPANY.email}`} className="sk-hit">
            {COMPANY.email}
          </a>
        </p>
        <p className={styles.asideText}>
          Prefer email? Write to us directly - the form goes to the same inbox.
        </p>
        {/*
         * The number is fetched, not printed - see src/content/company.ts for why
         * it is absent from this bundle entirely. The reCAPTCHA attribution this
         * control needs is carried by the form's footer on this same page, which
         * is the only page it may appear on.
         */}
        <div className={`${styles.asideDivider} sk-prose-link`}>
          Rather talk it through? <PhoneReveal />
        </div>
      </section>

      <section className={styles.asideCard} aria-labelledby="address-heading">
        <h2 id="address-heading" className={styles.asideHeading}>
          Registered address
        </h2>
        <address className={styles.asideAddress}>
          {COMPANY.legalName}
          <br />
          {COMPANY.address.street}
          <br />
          {COMPANY.address.locality}
          <br />
          {COMPANY.address.country}
        </address>
        <p className={styles.asideNote}>
          Our registered seat and address for correspondence. We work remotely - meetings are by
          video or by arrangement.
        </p>
        <div className={styles.asideRegistration}>
          IČO {COMPANY.ico}
          <br />
          DIČ {COMPANY.dic}
          <br />
          IČ DPH {COMPANY.icDph}
          <br />
          {COMPANY.register.short}
        </div>
      </section>

      <section className={styles.asideCard} aria-labelledby="hours-heading">
        <h2 id="hours-heading" className={styles.asideHeading}>
          When we are around
        </h2>
        <p className={styles.asideHours}>{COMPANY.openingHours}</p>
      </section>
    </div>
  );
}
